import React from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Button,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import AnalyticsChart from "../customChart/AnalaticsChart";

const ExamCenterDetailsTable = ({
  rows,
  columns,
  loading,
  isAdmin,
  cities,
  selectedCity,
  setSelectedCity,
  selectedEnrollment,
  setSelectedEnrollment,
  selectedAnalytics,
  selectedAttendance,
  setSelectedAttendance,
}) => {

  // Export functions remain same, just updated for button consistency
  const exportToExcel = () => {
    const rowsToExport = rows.map((row) => ({
      ...row,
      centerStatus: row.centerStatus ? "Mismatch" : "Synced",
    }));
    const worksheet = XLSX.utils.json_to_sheet(rowsToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Exam Center Details");
    XLSX.writeFile(workbook, "Exam_Center_Details.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Exam Center Details", 14, 15);
    const tableColumn = columns.map((col) => col.headerName);
    const tableRows = rows.map((row) =>
      columns.map((col) => {
        if (col.field === "centerStatus") return row.centerStatus ? "Mismatch" : "Synced";
        return row[col.field] !== undefined ? row[col.field] : "N/A";
      })
    );
    doc.autoTable({ head: [tableColumn], body: tableRows, startY: 20 });
    doc.save("Exam_Center_Details.pdf");
  };

  return (
    <Box
      sx={{
        padding: "24px",
        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        minHeight: "100vh",
      }}
    >
      {/* Header Section with Filters */}
      <Box
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.5)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 4, height: 32, borderRadius: 2, background: 'linear-gradient(to bottom, #7c3aed, #2563eb)' }} />
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
              Center Details
            </Typography>
          </Box>

          {isAdmin && (
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button 
                onClick={exportToExcel} 
                variant="outlined" 
                startIcon={<FileDownloadIcon />}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
              >
                Excel
              </Button>
              <Button
                onClick={exportToPDF}
                variant="outlined"
                color="warning"
                startIcon={<PictureAsPdfIcon />}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
              >
                PDF
              </Button>
            </Box>
          )}
        </Box>

        {/* Filter Controls */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Select City</InputLabel>
            <Select
              value={selectedCity}
              label="Select City"
              onChange={(e) => setSelectedCity(e.target.value)}
              sx={{ borderRadius: '10px', bgcolor: 'white' }}
            >
              {cities.map((city) => (
                <MenuItem key={city} value={city}>{city}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Enrollment</InputLabel>
            <Select
              value={selectedEnrollment}
              label="Enrollment"
              onChange={(e) => setSelectedEnrollment(e.target.value)}
              sx={{ borderRadius: '10px', bgcolor: 'white' }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="started">Started</MenuItem>
              <MenuItem value="not_started">Not Started</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Attendance</InputLabel>
            <Select
              value={selectedAttendance}
              label="Attendance"
              onChange={(e) => setSelectedAttendance(e.target.value)}
              sx={{ borderRadius: '10px', bgcolor: 'white' }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="started">Started</MenuItem>
              <MenuItem value="not_started">Not Started</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Data Grid Section */}
      <Box sx={{
        borderRadius: 4,
        background: 'rgba(255, 255, 255, 0.9)',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        overflow: 'hidden'
      }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          autoHeight
          getRowId={(row) => row.centerCode || row.sNo} // Fixes the ID error
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#eff6ff',
              borderBottom: '2px solid #bfdbfe',
              color: '#475569',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.8rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f1f5f9',
              py: 2,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f8fafc',
            },
          }}
        />
      </Box>

      {selectedAnalytics && (
        <Box sx={{ mt: 4, p: 3, bgcolor: 'white', borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Analytics: {selectedAnalytics.centerName}
          </Typography>
          <AnalyticsChart data={selectedAnalytics} />
        </Box>
      )}
    </Box>
  );
};

export default ExamCenterDetailsTable;